import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Zap,
  Lock,
  Cpu,
  FileText,
  Sliders,
  CheckCircle2,
  ArrowRight,
  Eye,
  EyeOff,
  Sparkles,
  Layers,
  Globe,
  Database,
  BarChart3,
  ChevronRight,
  Check,
  Shield,
  UserCheck,
  HelpCircle,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MARKETPLACE_APP_ID } from '@shared/contracts';

export const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const marketplaceUrl = (() => {
    const logtoAppId = import.meta.env.VITE_LOGTO_APP_ID;
    const appName = MARKETPLACE_APP_ID;

    if (logtoAppId && appName) {
      return `https://sensifai.com/marketplace/spi-${logtoAppId}/${appName}`;
    }
    return 'https://sensifai.com/marketplace';
  })();

  const billingUrl = 'https://sensifai.com/dashboard/billing';
  const [activeTab, setActiveTab] = useState<'medical' | 'financial' | 'identity'>('financial');

  const [maskType, setMaskType] = useState<'full' | 'partial' | 'synthetic'>('full');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Interactive demo sample data
  const sampleDocs = {
    financial: {
      title: 'Invoice_2026_98231.pdf',
      rawText: `INVOICE #98231\nDate: 2026-07-14\nClient: Johnathon E. Miller\nEmail: j.miller@acme-corp.com\nPhone: +1 (555) 019-2834\nAccount IBAN: US89370400440532013000\nTotal Due: $14,250.00\nCard ending: 4532-8901-2241-9081`,
      entities: [
        { type: 'NAME', val: 'Johnathon E. Miller' },
        { type: 'EMAIL', val: 'j.miller@acme-corp.com' },
        { type: 'PHONE', val: '+1 (555) 019-2834' },
        { type: 'IBAN', val: 'US89370400440532013000' },
        { type: 'CREDIT_CARD', val: '4532-8901-2241-9081' },
      ],
    },
    medical: {
      title: 'Patient_Record_HIPAA_04.pdf',
      rawText: `PATIENT DISCHARGE SUMMARY\nPatient: Sarah Jenkins\nDOB: 1988-04-12\nSSN: 901-44-8821\nPrimary Physician: Dr. Robert Vance\nDiagnosis: Acute Bronchitis (ICD-10 J20.9)\nPrescription: Amoxicillin 500mg\nEmergency Contact: Mark Jenkins (+1 555-014-9922)`,
      entities: [
        { type: 'NAME', val: 'Sarah Jenkins' },
        { type: 'DOB', val: '1988-04-12' },
        { type: 'SSN', val: '901-44-8821' },
        { type: 'DOCTOR', val: 'Dr. Robert Vance' },
        { type: 'PHONE', val: '+1 555-014-9922' },
      ],
    },
    identity: {
      title: 'UK_Passport_Application.pdf',
      rawText: `UK NATIONALITY DECLARATION\nApplicant: David Sterling\nNINO: QQ123456C\nPassport No: 918237401\nAddress: 42 Baker Street, London NW1 6XE\nTax ID: 884-902-110`,
      entities: [
        { type: 'NAME', val: 'David Sterling' },
        { type: 'NINO', val: 'QQ123456C' },
        { type: 'PASSPORT', val: '918237401' },
        { type: 'ADDRESS', val: '42 Baker Street, London NW1 6XE' },
        { type: 'TAX_ID', val: '884-902-110' },
      ],
    },
  };

  const currentSample = sampleDocs[activeTab];

  const getRedactedContent = () => {
    let text = currentSample.rawText;
    currentSample.entities.forEach((e) => {
      let replacement = '[REDACTED]';
      if (maskType === 'partial') {
        replacement = e.val.length > 6 ? e.val.substring(0, 3) + '••••' + e.val.slice(-2) : '••••';
      } else if (maskType === 'synthetic') {
        replacement = `<ANONYMIZED_${e.type}>`;
      }
      text = text.replace(e.val, replacement);
    });
    return text;
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/android-chrome-192x192.png" alt="SensiAvera Logo" className="w-8 h-8 sm:w-9 sm:h-9 object-contain rounded-xl" />
            <span className="font-bold text-lg sm:text-xl tracking-tight text-slate-900">
              Sensi<span className="text-sky-600">Avera</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-sky-600 transition-colors">Features</a>
            <a href="#demo" className="hover:text-sky-600 transition-colors">Live Demo</a>
            <a href="#compliance" className="hover:text-sky-600 transition-colors">Compliance</a>
            <a href="#pricing" className="hover:text-sky-600 transition-colors">Pricing</a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm font-semibold rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-md shadow-sky-600/20 transition-all flex items-center gap-2"
              >
                Go to App
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-semibold rounded-xl text-slate-700 hover:text-sky-600 hover:bg-slate-100 transition-all"
                >
                  Sign In
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-semibold rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-md shadow-sky-600/20 transition-all flex items-center gap-2"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label="Toggle navigation menu"
            >
              {mobileNavOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileNavOpen && (
          <div className="md:hidden border-b border-slate-200 bg-white px-4 pt-2 pb-6 space-y-4 shadow-xl animate-fadeIn">
            <div className="flex flex-col space-y-3 font-medium text-slate-700 text-sm border-b border-slate-100 pb-4">
              <a href="#features" onClick={() => setMobileNavOpen(false)} className="hover:text-sky-600 py-1">Features</a>
              <a href="#demo" onClick={() => setMobileNavOpen(false)} className="hover:text-sky-600 py-1">Live Demo</a>
              <a href="#compliance" onClick={() => setMobileNavOpen(false)} className="hover:text-sky-600 py-1">Compliance</a>
              <a href="#pricing" onClick={() => setMobileNavOpen(false)} className="hover:text-sky-600 py-1">Pricing</a>
            </div>

            <div className="flex flex-col gap-2.5 pt-1">
              {user ? (
                <button
                  onClick={() => { setMobileNavOpen(false); navigate('/dashboard'); }}
                  className="w-full py-2.5 text-sm font-semibold rounded-xl bg-sky-600 text-white flex items-center justify-center gap-2"
                >
                  Go to App
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileNavOpen(false)}
                    className="w-full py-2.5 text-center text-sm font-semibold rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setMobileNavOpen(false)}
                    className="w-full py-2.5 text-sm font-semibold rounded-xl bg-sky-600 text-white flex items-center justify-center gap-2"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-32 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-b from-sky-100/60 via-slate-50 to-transparent pointer-events-none -z-10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          {/* Version Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-sky-600 animate-pulse" />
            <span>SensiAvera v1.0 — Enterprise Document Intelligence & PII Protection</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight max-w-4xl mx-auto leading-[1.15]">
            Extract Insights, Redact PII, & Enforce{' '}
            <span className="bg-gradient-to-r from-sky-600 to-sky-400 bg-clip-text text-transparent">
              Compliance at Scale
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto font-normal leading-relaxed">
            Automate document processing with high-accuracy intelligent extraction, instant PII redaction, custom JSON validation, and zero-knowledge data retention.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <button
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              className="w-full sm:w-auto px-8 py-4 text-base font-semibold rounded-2xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/30 transition-all flex items-center justify-center gap-2 group"
            >
              <span>{user ? 'Open Workspace' : 'Start Free Trial'}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="#demo"
              className="w-full sm:w-auto px-8 py-4 text-base font-semibold rounded-2xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 transition-all flex items-center justify-center gap-2 shadow-sm"
            >
              <Eye className="w-5 h-5 text-sky-600" />
              <span>Interactive Live Demo</span>
            </a>
          </div>

          {/* Trust Highlights */}
          <div className="pt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto text-left">
            <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-sky-50 text-sky-600">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">99.8%</div>
                <div className="text-xs text-slate-500 font-medium">Redaction Accuracy</div>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">Zero Raw PII</div>
                <div className="text-xs text-slate-500 font-medium">Storage Guarantee</div>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-50 text-purple-600">
                <Cpu className="w-5 h-5" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">Native Engine</div>
                <div className="text-xs text-slate-500 font-medium">Real-time Vision & OCR</div>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">GDPR & HIPAA</div>
                <div className="text-xs text-slate-500 font-medium">Built-in Compliance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Live Demo Section */}
      <section id="demo" className="py-16 bg-white border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="text-center space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-600 bg-sky-50 px-3 py-1 rounded-full border border-sky-200">
              Live Product Experience
            </span>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
              Test Real-Time PII Extraction & Redaction
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto text-sm">
              Select a sample document and switch masking strategies to witness instant entity detection in action.
            </p>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
            {/* Top Bar Controls */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-slate-800 pb-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-400 font-medium mr-2">Sample Document:</span>
                {(['financial', 'medical', 'identity'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all capitalize ${
                      activeTab === tab
                        ? 'bg-sky-500 text-white shadow-md shadow-sky-500/30'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {tab} Document
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-400 font-medium">Masking Mode:</span>
                {(['full', 'partial', 'synthetic'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setMaskType(mode)}
                    className={`px-3 py-1 rounded-lg text-xs font-mono transition-all uppercase ${
                      maskType === mode
                        ? 'bg-sky-500/20 text-sky-300 border border-sky-400/50 font-bold'
                        : 'bg-slate-800/80 text-slate-400 border border-slate-700 hover:text-white'
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            {/* Split Screen View */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Panel: Raw Input with Highlighting */}
              <div className="bg-slate-950 rounded-2xl p-5 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between text-xs text-slate-400 pb-2 border-b border-slate-800">
                  <span className="font-semibold flex items-center gap-2 text-slate-200">
                    <FileText className="w-4 h-4 text-sky-400" />
                    {currentSample.title}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 font-mono text-[10px]">
                    Unprocessed PII Detected ({currentSample.entities.length})
                  </span>
                </div>
                <pre className="text-xs font-mono text-slate-300 whitespace-pre-wrap leading-relaxed min-h-[180px]">
                  {currentSample.rawText}
                </pre>
                <div className="pt-2 border-t border-slate-800 flex flex-wrap gap-1.5">
                  {currentSample.entities.map((e, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-800 text-sky-300 border border-slate-700"
                    >
                      {e.type}: {e.val}
                    </span>
                  ))}
                </div>
              </div>

              {/* Right Panel: Redacted Output */}
              <div className="bg-slate-950 rounded-2xl p-5 border border-sky-900/40 space-y-3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-sky-500/10 rounded-full blur-2xl pointer-events-none" />
                <div className="flex items-center justify-between text-xs text-slate-400 pb-2 border-b border-slate-800">
                  <span className="font-semibold flex items-center gap-2 text-emerald-400">
                    <ShieldCheck className="w-4 h-4" />
                    Sanitized Output ({maskType.toUpperCase()})
                  </span>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-[10px]">
                    100% Compliant
                  </span>
                </div>
                <pre className="text-xs font-mono text-emerald-300 whitespace-pre-wrap leading-relaxed min-h-[180px]">
                  {getRedactedContent()}
                </pre>
                <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
                  <span>Audit Event: <strong className="text-slate-200">#EVT-8921-OK</strong></span>
                  <button
                    onClick={() => navigate('/login')}
                    className="text-sky-400 hover:text-sky-300 font-semibold flex items-center gap-1 text-xs"
                  >
                    Try in your workspace <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section id="features" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="text-center space-y-3">
          <span className="text-xs font-bold uppercase tracking-wider text-sky-600 bg-sky-50 px-3 py-1 rounded-full border border-sky-200">
            Platform Features
          </span>
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Built for Privacy-First Document Intelligence
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-sm">
            Everything your team needs to ingest, analyze, redact, and validate document streams automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Card 1 */}
          <div className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <EyeOff className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Multi-Entity PII Redaction</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Detect credit cards, SSNs, national identity IDs, medical codes, and custom sensitive patterns across PDFs, scans, and text docs.
            </p>
          </div>

          {/* Card 2 */}
          <div className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <Cpu className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Intelligent Extraction Engine</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Advanced document parsing with multi-layered contextual recognition, structure detection, and automated validation pipelines.
            </p>
          </div>

          {/* Card 3 */}
          <div className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <Sliders className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Automated Validation Engine</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Enforce document schema validations, mathematical cross-checks, and custom business logic rules prior to data ingestion.
            </p>
          </div>

          {/* Card 4 */}
          <div className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <Globe className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Jurisdiction Compliance</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Out-of-the-box rule presets for EU GDPR, US HIPAA & CCPA, Canada PIPEDA, UK Data Protection Act, and PCI-DSS compliance.
            </p>
          </div>

          {/* Card 5 */}
          <div className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <Database className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Immutable Audit Trail</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Comprehensive audit logging for every document access, extraction event, policy change, and user session with cryptographic hashing.
            </p>
          </div>

          {/* Card 6 */}
          <div className="p-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <UserCheck className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Enterprise RBAC & SSO</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Granular role-based access control, team workspaces, API key management, and zero-trust identity verification.
            </p>
          </div>
        </div>
      </section>

      {/* Processing & Security Engines Showcase */}
      <section id="compliance" className="py-16 bg-slate-100/70 border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <span className="text-xs font-bold uppercase tracking-wider text-sky-600 bg-sky-50 px-3 py-1 rounded-full border border-sky-200">
                Security & Intelligence
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Enterprise-Grade Privacy & Processing Engines
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                Active Health Check
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Sensifai Vision OCR', type: 'Native OCR Engine', status: 'Active', ping: '8ms' },
              { name: 'PII Redactor Core', type: 'Data Sanitizer', status: 'Healthy', ping: '14ms' },
              { name: 'Validation Rule Engine', type: 'Schema Verifier', status: 'Ready', ping: '10ms' },
              { name: 'Audit Vault Ledger', type: 'Zero-Knowledge Crypto', status: 'Active', ping: '5ms' },
            ].map((engine, i) => (
              <div key={i} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center font-bold text-xs">
                    <Cpu className="w-4 h-4" />
                  </div>
                  <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                    {engine.ping}
                  </span>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{engine.name}</h4>
                  <p className="text-[11px] text-slate-500 font-medium">{engine.type}</p>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500 text-[11px]">Status</span>
                  <span className="text-emerald-600 font-semibold text-[11px] flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {engine.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 mb-12">
          <span className="text-xs font-bold uppercase tracking-wider text-sky-600 bg-sky-50 px-3 py-1 rounded-full border border-sky-200">
            Flexible Pricing
          </span>
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Plans for Every Scale
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto text-sm">
            SensiAvera is distributed through the Sensifai Marketplace. View live pricing, available plans, and subscribe directly on the marketplace.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Flat-rate / Subscription */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200 shadow-sm flex flex-col gap-5">
            <div className="w-10 h-10 rounded-2xl bg-sky-50 border border-sky-100 flex items-center justify-center">
              <Layers className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 mb-1">Flat-Rate Subscriptions</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Predictable monthly billing — pick a plan and pay a fixed recurring fee regardless of volume.
              </p>
            </div>
            <ul className="space-y-2 text-xs text-slate-600 flex-1">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-600 shrink-0" /> Fixed monthly or annual billing</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-600 shrink-0" /> Optional free trial period</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-600 shrink-0" /> Managed via Sensifai Marketplace</li>
            </ul>
          </div>

          {/* Pay-as-you-go */}
          <div className="p-8 rounded-3xl bg-slate-900 text-white shadow-xl flex flex-col gap-5 relative border border-sky-500/30">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-sky-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
              Most Flexible
            </div>
            <div className="w-10 h-10 rounded-2xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-sky-400" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white mb-1">Prepaid Pay-As-You-Go</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Load credits into your prepaid wallet and pay only for what you use — usage deducts in real time.
              </p>
            </div>
            <ul className="space-y-2 text-xs text-slate-300 flex-1">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-400 shrink-0" /> Per-unit metered billing</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-400 shrink-0" /> Flat or tiered rate per unit</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-400 shrink-0" /> Real-time wallet deduction</li>
            </ul>
          </div>

          {/* Tiered / Package */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200 shadow-sm flex flex-col gap-5">
            <div className="w-10 h-10 rounded-2xl bg-sky-50 border border-sky-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 mb-1">Tiered & Package Plans</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Volume-based tiers or fixed-size bundles — cost scales progressively with your usage.
              </p>
            </div>
            <ul className="space-y-2 text-xs text-slate-600 flex-1">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-600 shrink-0" /> Tiered volume or graduated rates</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-600 shrink-0" /> Package bundles (e.g. seats)</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sky-600 shrink-0" /> Quantity chosen at checkout</li>
            </ul>
          </div>
        </div>

        {/* Marketplace CTA */}
        <div className="mt-10 text-center">
          <a
            href={marketplaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-2xl shadow-lg shadow-sky-600/20 text-sm transition-all"
          >
            <span>View Live Pricing on Marketplace</span>
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="mt-3 text-xs text-slate-400">All plans and pricing are managed through the Sensifai Marketplace.</p>
        </div>
      </section>

      {/* CTA Footer Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-r from-sky-600 to-sky-700 text-white p-8 sm:p-12 text-center space-y-6 shadow-xl relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-4 relative z-10">
            <h2 className="text-3xl font-extrabold tracking-tight">Ready to Automate Your Document Security?</h2>
            <p className="text-sky-100 text-sm leading-relaxed">
              Join leading organizations using SensiAvera to protect sensitive customer data and enforce compliance seamlessly.
            </p>
            <div className="pt-2 flex justify-center">
              <button
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                className="px-8 py-4 bg-white text-sky-700 hover:bg-sky-50 font-bold rounded-2xl shadow-lg text-sm transition-all flex items-center gap-2"
              >
                <span>Launch Workspace Now</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200/80 py-12 text-slate-500 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/android-chrome-192x192.png" alt="SensiAvera Logo" className="w-7 h-7 object-contain rounded-lg" />
            <span className="font-bold text-slate-900 text-sm">SensiAvera</span>
            <span className="text-[11px] text-slate-400">© 2026 Sensifai. All rights reserved.</span>
          </div>

          <div className="flex items-center gap-6">
            <a href="#features" className="hover:text-slate-800">Features</a>
            <a href="#compliance" className="hover:text-slate-800">Security & Privacy</a>
            <a href="#pricing" className="hover:text-slate-800">Pricing</a>
            <Link to="/login" className="hover:text-slate-800">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
